import { formatPrice, getNextTierSavings } from '@/lib/pricing';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sparkles, TrendingUp, Gift } from 'lucide-react';
import productData from '@/data/products';

export function CartSummary({ summary }) {
  const {
    subtotal,
    totalItems,
    bulkDiscountAmount,
    bulkDiscountInfo,
    discountCodeAmount,
    discountCode,
    shipping,
    total,
    totalSavings,
  } = summary;

  // 計算下一級優惠可以省下的金額
  const nextTierInfo = getNextTierSavings(
    totalItems,
    productData.pricing.salePrice,
    productData.bulkDiscounts
  );

  return (
    <div className="space-y-4">
      {/* 商品數量 */}
      <div className="flex justify-between text-sm text-gray-600">
        <span>商品數量</span>
        <span>{totalItems} 件</span>
      </div>

      {/* 小計 */}
      <div className="flex justify-between">
        <span className="text-gray-700">小計</span>
        <span className="font-medium">{formatPrice(subtotal)}</span>
      </div>

      {/* 團購優惠 */}
      {bulkDiscountInfo.applicable && bulkDiscountAmount > 0 && (
        <div className="flex justify-between text-green-600">
          <div className="flex items-center gap-1">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm">團購優惠（{bulkDiscountInfo.message}）</span>
          </div>
          <span className="font-medium">-{formatPrice(bulkDiscountAmount)}</span>
        </div>
      )}

      {/* 折扣碼優惠 */}
      {discountCode && discountCodeAmount > 0 && (
        <div className="flex justify-between text-blue-600">
          <div className="flex items-center gap-1">
            <span className="text-sm">折扣碼</span>
            <Badge variant="outline" className="text-xs">
              {discountCode.code}
            </Badge>
          </div>
          <span className="font-medium">-{formatPrice(discountCodeAmount)}</span>
        </div>
      )}

      {/* 運費 */}
      <div className="flex justify-between text-sm">
        <span className="text-gray-700">運費</span>
        <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
          {shipping === 0 ? '免運費' : formatPrice(shipping)}
        </span>
      </div>

      <Separator />

      {/* 總計 */}
      <div className="flex justify-between items-baseline">
        <span className="text-lg font-semibold text-gray-900">總計</span>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">
            {formatPrice(total)}
          </div>
          {totalSavings > 0 && (
            <div className="text-xs text-green-600">
              已省 {formatPrice(totalSavings)}
            </div>
          )}
        </div>
      </div>

      {/* 下一個優惠門檻提示 - 增強版 */}
      {nextTierInfo && (
        <Alert className="border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
          <TrendingUp className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-sm">
            <div className="space-y-1">
              <p className="font-semibold text-orange-900">
                🎁 再加購 {nextTierInfo.needToBuy} 件，享更多優惠！
              </p>
              <p className="text-orange-800">
                升級至 {nextTierInfo.nextThreshold} 件享 <strong>{Math.round(nextTierInfo.nextRate * 10)} 折</strong>，
                <strong className="text-orange-600"> 可再省 {formatPrice(nextTierInfo.savings)}</strong>
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* 已享有最高優惠提示 */}
      {!nextTierInfo && bulkDiscountInfo.applicable && (
        <Alert className="border-green-200 bg-green-50">
          <Gift className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-sm text-green-800">
            🎉 恭喜！您已享有最高團購優惠（{Math.round(bulkDiscountInfo.rate * 10)} 折）
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
